import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  Spinner,
  makeStyles,
} from "@fluentui/react-components";
import { useForm } from "react-hook-form";
import { AppDivider, AppSelect } from "../common";
import listStyles from "./EmplyeesListStyles";
import {
  formatResourceAllocationDuration,
  parseResourceAllocationDurationInput,
} from "../../utils/resourceAllocationDuration";
import type { IResourceAllocationOrderApi, IResourceAllocationProjectApi } from "../../services/types/resourceAllocation";

export type ResourceAllocationOrderDialogMode = "add" | "edit";

interface IFormData {
  youTrackProjectId: string;
  assignmentDescription: string;
  duration: string;
}

interface ResourceAllocationOrderDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  mode: ResourceAllocationOrderDialogMode;
  selectedMonth: string;
  selectedYear: number;
  projects: IResourceAllocationProjectApi[];
  initialProjectId?: string;
  order?: IResourceAllocationOrderApi | null;
  onSubmitAdd: (payload: {
    assignmentDescription: string;
    durationMinutes: number;
    youTrackProjectId: string;
    youTrackProjectLabel: string;
  }) => Promise<void>;
  onSubmitEdit: (payload: {
    youTrackProjectId: string;
    youTrackProjectLabel: string;
    assignmentDescription: string;
    durationMinutes: number;
  }) => Promise<void>;
  isSubmitting: boolean;
}

const useStyles = makeStyles({
  dialogContainer: {
    maxWidth: "450px",
  },
  formContent: {
    display: "flex",
    flexDirection: "column",
  },
  selectFullWidth: {
    width: "100%",
    minWidth: "280px",
  },
  validationError: {
    color: "#bc2f32",
    fontSize: "12px",
    marginTop: "4px",
  },
  emptyProjectsHint: {
    color: "#616161",
    fontSize: "13px",
    marginTop: "4px",
  },
});

const ResourceAllocationOrderDialog: React.FC<ResourceAllocationOrderDialogProps> = ({
  open,
  setOpen,
  mode,
  selectedMonth,
  selectedYear,
  projects,
  initialProjectId,
  order,
  onSubmitAdd,
  onSubmitEdit,
  isSubmitting,
}) => {
  const styles = useStyles();
  const [validationError, setValidationError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: {
      youTrackProjectId: "",
      assignmentDescription: "",
      duration: "",
    },
  });

  const projectOptions = projects.map((project) => ({
    value: project.youTrackProjectId,
    label: project.projectName,
  }));

  useEffect(() => {
    if (!open) {
      return;
    }

    setValidationError("");
    reset({
      youTrackProjectId: initialProjectId ?? order?.youTrackProjectId ?? "",
      assignmentDescription: order?.assignmentDescription ?? "",
      duration: order ? formatResourceAllocationDuration(order.durationMinutes) : "",
    });
  }, [open, initialProjectId, order, reset]);

  const handleClose = () => {
    setOpen(false);
    setValidationError("");
    reset();
  };

  const onSubmit = async (data: IFormData) => {
    const selectedProject = projects.find((p) => p.youTrackProjectId === data.youTrackProjectId);
    if (!selectedProject) {
      setValidationError("Wybierz projekt.");
      return;
    }

    try {
      if (!data.assignmentDescription.trim()) {
        setValidationError("Nazwa zlecenia jest wymagana.");
        return;
      }

      const durationMinutes = parseResourceAllocationDurationInput(data.duration);
      if (durationMinutes === null) {
        setValidationError("Podaj ilość godzin, np. 168h, 30h, 167h 30min lub 168:00.");
        return;
      }

      setValidationError("");

      if (mode === "add") {
        await onSubmitAdd({
          assignmentDescription: data.assignmentDescription.trim(),
          durationMinutes,
          youTrackProjectId: selectedProject.youTrackProjectId,
          youTrackProjectLabel: selectedProject.projectName,
        });
      } else {
        await onSubmitEdit({
          youTrackProjectId: selectedProject.youTrackProjectId,
          youTrackProjectLabel: selectedProject.projectName,
          assignmentDescription: data.assignmentDescription.trim(),
          durationMinutes,
        });
      }

      handleClose();
    } catch {
      // Error handled in the service (toast); modal remains open.
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogContainer}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody>
            <DialogTitle>{mode === "add" ? "Dodaj zlecenie" : "Edytuj zlecenie"}</DialogTitle>
            <DialogContent className={styles.formContent}>
              <div style={listStyles.dialogFormField}>
                <Label>Okres</Label>
                <span style={listStyles.dialogInfoText}>
                  {selectedMonth} {selectedYear}
                </span>
              </div>
              <AppDivider />

              <div style={listStyles.dialogFormField}>
                <AppSelect
                  label="Projekt (YouTrack)"
                  optionEntries={projectOptions}
                  formHandler={register("youTrackProjectId", { required: true })}
                  className={styles.selectFullWidth}
                  disabled={projects.length === 0}
                />
                {projects.length === 0 ? (
                  <div className={styles.emptyProjectsHint}>
                    Brak wspólnych projektów do wyboru.
                  </div>
                ) : null}
              </div>

              <AppDivider />
              <div style={listStyles.dialogFormField}>
                <Label required>Nazwa zlecenia</Label>
                <Input
                  {...register("assignmentDescription", { required: true })}
                />
                {errors.assignmentDescription ? (
                  <div className={styles.validationError}>Nazwa zlecenia jest wymagana.</div>
                ) : null}
              </div>
              <AppDivider />
              <div style={listStyles.dialogFormField}>
                <Label required htmlFor="resource-allocation-duration">
                  Ilość godzin
                </Label>
                <Input
                  id="resource-allocation-duration"
                  {...register("duration", { required: true })}
                  placeholder="np. 168h"
                />
                <div style={listStyles.dialogFieldHint}>
                  np. 168h, 30h, 167h 30min lub 168:00
                </div>
                {errors.duration ? (
                  <div className={styles.validationError}>Ilość godzin jest wymagana.</div>
                ) : null}
              </div>

              {validationError ? (
                <div className={styles.validationError}>{validationError}</div>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" type="button" onClick={handleClose} disabled={isSubmitting}>
                Anuluj
              </Button>
              <Button appearance="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spinner size="tiny" /> : null}
                Zapisz
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default ResourceAllocationOrderDialog;
